import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { SearchActions, SearchState } from "src/search/types";
import { useSearch } from "src/search/useSearchContext";
import { asKey } from "src/utils/fields";
import { attributeToTitle } from "src/utils/strings";
import { QuestionInput } from "src/search/components/inputs/QuestionInput";
import { serializeParams } from "src/search/lib/params-state-reducer";
import { useApplicationContext } from "src/hooks/useApplication";
import { ExploreContext } from "./useExploreContext";
import { ContentViews } from "./lib/useContentView";

export function ParamsPanel({
  allowNoopSave,
  context,
  disableDateRange,
  disableCompareWith,
  alternateStyle,
  expandOnFocus,
  floatAway,
  collapsed: defaultCollapsed = false,
  ...props
}: {
  context: ExploreContext;
  allowNoopSave?: boolean;
  applyChangesLabel?: string;
  disableDateRange?: boolean;
  disableCompareWith?: boolean;
  noTypeSwitching?: boolean;
  preventApply?: boolean;
  alternateStyle?: boolean;
  expandOnFocus?: boolean;
  floatAway?: boolean;
  collapsed?: boolean;
}) {
  const [isCollapsed] = useState(defaultCollapsed);
  const orgId = useApplicationContext().org_id!;
  const { params: upstream, merge, doc, view } = context;

  // Memoize the initial state to prevent unnecessary JSON parsing
  const initialState = useMemo(
    () => JSON.parse(JSON.stringify(upstream.state)),
    [upstream.state]
  );

  // Memoize the working context to prevent unnecessary re-creation
  const working = useSearch(initialState, ...merge);

  // Track if we're currently processing an update
  const isProcessingUpdate = useRef(false);

  // Memoize derived state
  const isDifferent = useMemo(
    () => upstream.serialized !== working.params.serialized,
    [upstream.serialized, working.params.serialized]
  );

  const validFilters = useMemo(() => {
    const validStringsFilters = (working.params.state.filters || [])
      .filter((f) => Object.keys(f.comparison)[0] !== "WITHIN")
      .every((f) => Object.values(f.comparison)[0].length > 0);

    const validNumbersFilters = (working.params.state.filters || [])
      .filter((f) => Object.keys(f.comparison)[0] === "WITHIN")
      .every(
        (f) =>
          !Object.values(f.comparison)[0].every(
            (val) => val === "__VZBL::null__"
          )
      );

    return validStringsFilters && validNumbersFilters;
  }, [working.params.state.filters]);

  // Memoize the working state value
  const workingValue = useMemo(
    () => ({
      ...working.params.state,
      orgId,
    }),
    [working.params.state, orgId]
  );

  // Memoize options
  const options = useMemo(
    () => ({
      disableDateRange,
      disableCompareWith:
        disableCompareWith || !upstream.selectors.isComparable(upstream.state),
    }),
    [disableDateRange, disableCompareWith, upstream.selectors, upstream.state]
  );

  const dispatch = useRef(working.params.dispatch);
  dispatch.current = working.params.dispatch;
  const submit = useRef(upstream.dispatch);
  submit.current = upstream.dispatch;

  // Memoize selecting and grouping calculations
  const { selecting, grouping, differentType } = useMemo(() => {
    const selecting = (working.params.state.select || []).join(",");
    const grouping = (working.params.state.groupBys || [])
      .map((g) => attributeToTitle(asKey(g)))
      .join(",");
    const differentType =
      selecting !== (upstream.state.select || []).join(",") ||
      grouping !==
        (upstream.state.groupBys || [])
          .map((g) => attributeToTitle(asKey(g)))
          .join(",");

    return { selecting, grouping, differentType };
  }, [
    working.params.state.select,
    working.params.state.groupBys,
    upstream.state.select,
    upstream.state.groupBys,
  ]);

  const apply = useCallback(() => {
    submit.current({
      type: SearchActions.loadState,
      value: working.params.serialized,
    });
    if (differentType && !props.noTypeSwitching) {
      const selectingLabel = attributeToTitle(selecting || grouping || "Event");
      doc.setTitle(`Selecting "${selectingLabel}"`);
      view.set(ContentViews.default);
      view.setConfig({});
    }
  }, [
    working.params.serialized,
    differentType,
    props.noTypeSwitching,
    doc,
    view,
    selecting,
    grouping,
  ]);

  // Debounced onChange handler to prevent rapid updates
  const handleChange = useCallback(
    (state: SearchState) => {
      if (isProcessingUpdate.current) return;
      isProcessingUpdate.current = true;

      // Use RAF to batch updates
      requestAnimationFrame(() => {
        const cloned = { ...state };
        if (!working.params.selectors.isComparable(state)) {
          cloned.compareWith = undefined;
        }
        working.params.dispatch({
          type: SearchActions.loadState,
          value: serializeParams(cloned),
        });
        isProcessingUpdate.current = false;
      });
    },
    [working.params]
  );

  // Sync effect with cleanup
  useEffect(() => {
    let isCancelled = false;

    if (!isCancelled) {
      working.params.dispatch({
        type: SearchActions.loadState,
        value: upstream.serialized,
      });
    }

    return () => {
      isCancelled = true;
    };
  }, [upstream.serialized]);

  return (
    <QuestionInput
      alternateStyle={alternateStyle}
      expandOnFocus={expandOnFocus}
      floatAway={floatAway}
      collapsed={isCollapsed}
      options={options}
      seed={upstream.serialized}
      onApply={isDifferent ? apply : undefined}
      disabledApply={!isDifferent}
      applyLabel={isDifferent ? "Apply Changes" : "No Changes"}
      disabledCancel={!isDifferent}
      value={workingValue}
      onChange={handleChange}
    />
  );
}
